import {
  FlexColumn,
  FlexRow,
  IMAGE_EXTENSIONS,
  LabeledInput,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { Banner } from 'baseui/banner';
import { ProgressSteps, Step } from 'baseui/progress-steps';
import { LabelSmall, LabelXSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../../../constants/app';
import { useFeedback } from '../../../../hooks/useFeedback';
import { useStyles } from '../../../../hooks/useStyles';
import {
  createFleetDriver,
  createFleetDriverPhoto,
  updateFleetDriver,
  verifyFleetDriverPhoto,
} from '../../../../services/fleetService';
import { apiParamsState } from '../../../../storage/app';
import { FleetDriver, FleetDriverRequest } from '../../../../types/drivers';
import DocumentUploader from '../../../shared/DocumentUploader';
import LocalizedDatePicker from '../../../shared/LocalizedDatePicker';
import { LabeledEmailInput } from '../../../ui/LabeledEmailInput';
import { LabeledPhoneInput } from '../../../ui/LabeledPhoneInput';
import FleetDriverAvatar from './FleetDriverAvatar';

type Props = {
  driver?: FleetDriver;
  onCancel: () => void;
  afterSave: () => void;
  onMatch?: (
    existingDriver:
      | (FleetDriver & {
          newPhoto: string;
        })
      | undefined,
  ) => void;
};

function FleetDriverForm({ driver, onCancel, afterSave, onMatch }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const [currentStep, setCurrentStep] = useState(0);
  const [emailIsValid, setEmailIsValid] = useState(true);
  const [phoneIsValid, setPhoneIsValid] = useState(true);
  const apiParams = useRecoilValue(apiParamsState);
  const [editingDriver, setEditingDriver] = useState<FleetDriverRequest>({
    name: '',
    lastName: '',
    email: undefined,
    licenseExpiry: undefined,
    licenseNumber: undefined,
    phone: undefined,
    photo: undefined,
    photoFile: undefined,
  });
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { startLoading: startVerifying, stopLoading: stopVerifying, loading: verifying } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const photoFileIsValid = editingDriver.photoFile?.status === 'processed';

  useEffect(() => {
    if (driver) {
      setCurrentStep(1);
      setEditingDriver({
        name: driver.name,
        lastName: driver.lastName,
        email: driver.email,
        licenseExpiry: driver.licenseExpiry,
        licenseNumber: driver.licenseNumber,
        phone: driver.phone,
        photo: driver.photo,
        photoFile: undefined,
      });
    }
  }, [driver]);

  const canSubmit = useMemo(() => {
    return !!editingDriver.name && !!editingDriver.lastName && !!editingDriver.photo && emailIsValid && phoneIsValid;
  }, [editingDriver, emailIsValid, phoneIsValid]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof FleetDriverRequest) => {
    setEditingDriver((prev) => ({ ...prev, [field]: value }));
  };

  const onSave = async () => {
    if (!apiParams) return;
    startLoading();
    const { photo, photoFile, ...sharedProps } = editingDriver;
    const sharedParams = {
      clientId: apiParams.clientId,
      subClientId: apiParams.subClientId,
      data: { ...sharedProps },
    };
    const { id, error } = driver
      ? await updateFleetDriver({ ...sharedParams, driverId: driver._id })
      : await createFleetDriver({
          ...sharedParams,
          data: { ...sharedParams.data, photo },
        });
    if (!error && id) {
      afterSave();
      showPositiveFeedback(
        driver
          ? FEEDBACK.edited(FEEDBACK_PREFIXES.driver, `${editingDriver.name} ${editingDriver.lastName}`)
          : FEEDBACK.created(FEEDBACK_PREFIXES.driver),
      );
    } else {
      showFailFeedback(
        error || driver
          ? FEEDBACK.failedEdition(FEEDBACK_PREFIXES.driver, `${editingDriver.name} ${editingDriver.lastName}`)
          : FEEDBACK.failedCreation(FEEDBACK_PREFIXES.driver),
      );
    }
    stopLoading();
  };

  const verifyPhoto = async () => {
    if (!apiParams || !editingDriver.photoFile) return;
    startVerifying();
    const sharedQueryParams = {
      clientId: apiParams.clientId,
      subClientId: apiParams.subClientId,
    };
    const { key, error: photoError } = await createFleetDriverPhoto({
      ...sharedQueryParams,
      file: editingDriver.photoFile.file,
    });
    if (key && !photoError) {
      setEditingDriver((prev) => ({
        ...prev,
        photo: key,
      }));
      const { driver: existingDriver, error: verifyError } = await verifyFleetDriverPhoto({
        ...sharedQueryParams,
        fileKey: key,
      });
      if (existingDriver) {
        onMatch?.({ ...existingDriver, newPhoto: key });
      } else if (verifyError) {
        showFailFeedback(verifyError);
      } else {
        setCurrentStep(1);
      }
    } else {
      showFailFeedback('Ocurrió un error al subir la foto, inténtalo más tarde.');
    }
    stopVerifying();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <ProgressSteps
        current={currentStep}
        overrides={{
          Root: {
            style: {
              overflow: 'auto',
              width: '100%',
              height: '100%',
            },
          },
        }}
        alwaysShowDescription={currentStep > 0}
      >
        <Step title="Foto del conductor">
          {currentStep === 0 && (
            <>
              <DocumentUploader
                file={editingDriver.photoFile}
                onFileChange={(file) => {
                  onInputChange(file, 'photoFile');
                }}
                acceptExtensions={IMAGE_EXTENSIONS}
              />
              <StyledButton
                size="compact"
                onClick={verifyPhoto}
                isLoading={verifying}
                disabled={!photoFileIsValid}
                full
              >
                Verificar existencia
              </StyledButton>
            </>
          )}
          {currentStep === 1 && (
            <FleetDriverAvatar
              name={driver?.name || ''}
              lastName={driver?.lastName || ''}
              photo={driver?.photo}
              size="100px"
              thumbnail={editingDriver.photoFile?.imagePreviewThumbnail}
            />
          )}
        </Step>
        <Step title="Datos del conductor">
          <FlexColumn
            classNames={css({
              overflow: 'auto',
              width: '100%',
              height: '100%',
              flexDirection: 'column',
            })}
          >
            {!driver && (
              <Banner
                overrides={{
                  Root: {
                    style: {
                      marginTop: 0,
                      marginBottom: 0,
                      marginLeft: 0,
                      marginRight: 0,
                    },
                  },
                }}
              >
                <LabelXSmall color={theme.colors.contentSecondary}>
                  No se encontró un conductor, ingresa su información para agregarlo a tu flota.
                </LabelXSmall>
              </Banner>
            )}
            <LabeledInput
              label="Nombre(s):"
              value={editingDriver?.name}
              onChange={(value) => onInputChange(value, 'name')}
              required
              placeholder="Ingresa el/los nombre(s)"
            />
            <LabeledInput
              label="Apellidos:"
              value={editingDriver?.lastName}
              onChange={(value) => onInputChange(value, 'lastName')}
              required
              placeholder="Ingresa los apellidos"
            />
            <LabeledEmailInput
              placeholder="Ingresa un correo electrónico"
              value={editingDriver.email}
              onChange={(value) => {
                onInputChange(value, 'email');
              }}
              isEmailValid={setEmailIsValid}
            />
            <LabeledPhoneInput
              placeholder="Ingresa un número telefónico"
              value={editingDriver.phone}
              onChange={(value) => {
                onInputChange(value, 'phone');
              }}
              type="simple"
              isPhoneValid={setPhoneIsValid}
            />
            <LabeledInput
              label="Número de licencia:"
              value={editingDriver?.licenseNumber}
              onChange={(value) => onInputChange(value, 'licenseNumber')}
              placeholder="Ingresa el número de licencia"
            />
            <FlexColumn gap={theme.sizing.scale200}>
              <LabelSmall>Expiración de licencia</LabelSmall>
              <LocalizedDatePicker
                value={editingDriver.licenseExpiry ? [new Date(editingDriver.licenseExpiry)] : []}
                onChange={({ date }) => {
                  if (date && !Array.isArray(date)) {
                    onInputChange(date.getTime(), 'licenseExpiry');
                  }
                }}
                onClose={() => {
                  onInputChange(editingDriver.licenseExpiry, 'licenseExpiry');
                }}
                size="compact"
              />
            </FlexColumn>
          </FlexColumn>
        </Step>
      </ProgressSteps>

      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {driver?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}

export default FleetDriverForm;
